import React, { useState, useEffect } from "react";
import { 
  Calendar, 
  CheckCircle, 
  RefreshCw, 
  LogOut, 
  AlertCircle, 
  Sparkles, 
  ExternalLink,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { 
  initWorkspaceAuth, 
  signInWithGoogleWorkspace, 
  logoutWorkspace, 
  createGoogleCalendarEvent 
} from "../lib/firebase";
import { User } from "firebase/auth";
import { Appointment, Patient } from "../types";

interface GoogleCalendarSyncProps {
  appointments: Appointment[];
  patients: Patient[];
  apiToken: string;
}

export default function GoogleCalendarSync({ appointments = [], patients = [], apiToken }: GoogleCalendarSyncProps) {
  const [googleUser, setGoogleUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSyncing, setIsSyncing] = useState<string | null>(null); // maps appt.id to progress
  const [syncedEventLinks, setSyncedEventLinks] = useState<{ [apptId: string]: string }>({});
  const [showConfirmation, setShowConfirmation] = useState<{ appt: any } | null>(null);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(() => {
    return localStorage.getItem("clinic_calendar_autosync") === "true";
  });
  const [isExpanded, setIsExpanded] = useState(true);
  const [showSuccessToast, setShowSuccessToast] = useState<string | null>(null);

  // Initialize Auth state listeners and retrieve the active in-memory token
  useEffect(() => {
    const unsubscribe = initWorkspaceAuth(
      (user, token) => {
        setGoogleUser(user);
        setAccessToken(token);
        setIsAuthenticated(true);
      },
      () => {
        setGoogleUser(null);
        setAccessToken(null);
        setIsAuthenticated(false);
      }
    );

    // Retrieve cached list of synced appointments from local storage for seamless indicators
    try {
      const savedLinks = localStorage.getItem("clinic_synced_calendar_events");
      if (savedLinks) {
        setSyncedEventLinks(JSON.parse(savedLinks));
      }
    } catch (e) {
      console.error("Failed to parse synced calendar events database:", e);
    }

    return () => unsubscribe();
  }, []);

  const handleConnect = async () => {
    setIsLoggingIn(true);
    try {
      const result = await signInWithGoogleWorkspace();
      if (result) {
        setGoogleUser(result.user);
        setAccessToken(result.accessToken);
        setIsAuthenticated(true);
        triggerToast("تم ربط حساب Google بنجاح لتنسيق المواعيد!");
      }
    } catch (err: any) {
      console.error(err);
      alert(`خطأ أثناء عملية المصادقة: ${err.message || 'يرجى مراجعة إعدادات الأمان.'}`);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm("هل أنت متأكد من رغبتك في إلغاء ربط تقويم Google الخاص بالعيادة؟")) {
      await logoutWorkspace();
      setGoogleUser(null);
      setAccessToken(null);
      setIsAuthenticated(false);
      triggerToast("تم إلغاء ربط الحساب بنجاح.");
    }
  };

  const triggerToast = (msg: string) => {
    setShowSuccessToast(msg);
    setTimeout(() => setShowSuccessToast(null), 4000);
  };

  // Persists the auto-sync state
  const toggleAutoSync = () => {
    const nextVal = !autoSyncEnabled;
    setAutoSyncEnabled(nextVal);
    localStorage.setItem("clinic_calendar_autosync", nextVal ? "true" : "false");
    triggerToast(nextVal ? "تم تفعيل المزامنة التلقائية فور حجز المواعيد الجديدة." : "تم إلغاء المزامنة التلقائية.");
  };

  // Perform Google Calendar event synchronization following the mandatory prompt confirmation
  const executeSync = async (appt: Appointment) => {
    if (!accessToken) return;

    const patient = patients.find(p => p.id === appt.patientId);
    const patientName = patient ? patient.name : "مراجع غير معروف";
    const physicianName = appt.physicianId === "dr.ali@clinic.iq" ? "طبيبة أسنان وتجميل - د. جمانة مصطفى الجلبي" : "د.واثق عبدالخالق الجلبي";

    setIsSyncing(appt.id);
    const result = await createGoogleCalendarEvent(accessToken, {
      id: appt.id,
      patientName,
      department: appt.department,
      physicianName,
      dateTime: appt.dateTime,
    });

    if (result.success && result.eventUrl) {
      const updatedLinks = { ...syncedEventLinks, [appt.id]: result.eventUrl };
      setSyncedEventLinks(updatedLinks);
      localStorage.setItem("clinic_synced_calendar_events", JSON.stringify(updatedLinks));
      triggerToast(`تمت مزامنة موعد المريض (${patientName}) بنجاح إلى تقويم Google!`);
    } else {
      if (result.error && (result.error.includes("expired") || result.error.includes("invalid authentication credentials") || result.error.includes("401"))) { setAccessToken(null); setIsAuthenticated(false); try { localStorage.removeItem("clinic_workspace_access_token"); } catch(e) {} alert("انتهت صلاحية جلسة Google، يرجى إعادة تسجيل الدخول لمزامنة المواعيد."); } else { alert(`فشلت مزامنة الموعد: ${result.error || "تأكد من صلاحيات الحساب"}`); }
    }
    setIsSyncing(null);
    setShowConfirmation(null);
  };

  // Handle auto-sync triggers for new appointments from parent components if configured
  useEffect(() => {
    if (!isAuthenticated || !accessToken || !autoSyncEnabled || appointments.length === 0) return;

    const latestAppt = appointments[appointments.length - 1];
    if (latestAppt && !syncedEventLinks[latestAppt.id] && isSyncing !== latestAppt.id) {
      // Execute background auto-sync without blocking receptionists
      const patient = patients.find(p => p.id === latestAppt.patientId);
      const patientName = patient ? patient.name : "مراجع غير معروف";
      const physicianName = latestAppt.physicianId === "dr.ali@clinic.iq" ? "طبيبة أسنان وتجميل - د. جمانة مصطفى الجلبي" : "د.واثق عبدالخالق الجلبي";

      createGoogleCalendarEvent(accessToken, {
        id: latestAppt.id,
        patientName,
        department: latestAppt.department,
        physicianName,
        dateTime: latestAppt.dateTime,
      }).then(res => {
        if (res.success && res.eventUrl) {
          const updated = { ...syncedEventLinks, [latestAppt.id]: res.eventUrl };
          setSyncedEventLinks(updated);
          localStorage.setItem("clinic_synced_calendar_events", JSON.stringify(updated));
        }
      }).catch(console.error);
    }
  }, [appointments.length, isAuthenticated, accessToken, autoSyncEnabled]);

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden shadow-sm transition-all duration-300">
      
      {/* Widget Header */}
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-slate-50 border-b border-[#E2E8F0] px-5 py-3.5 flex justify-between items-center cursor-pointer hover:bg-slate-100/75 select-none transition"
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-indigo-600" />
          <h3 className="font-bold text-[#1E293B] text-xs uppercase tracking-wide">
            مزامنة تقويم Google Calendar
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <span className="bg-emerald-50 text-emerald-700 text-[8px] font-bold px-2 py-0.5 rounded-full border border-emerald-200">
              متصل
            </span>
          ) : (
            <span className="bg-slate-100 text-slate-500 text-[8px] font-bold px-2 py-0.5 rounded-full border border-slate-200">
              غير نشط
            </span>
          )}
          {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </div>
      </div>

      {isExpanded && (
        <div className="p-5 space-y-4">
          
          {/* Active Toast Notification */}
          {showSuccessToast && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] p-3 rounded-lg flex items-center gap-1.5 animate-in fade-in duration-300">
              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>{showSuccessToast}</span>
            </div>
          )}

          {/* Conditional View: Not Connected to Google Auth */}
          {!isAuthenticated ? (
            <div className="space-y-3.5 text-center py-2">
              <div className="bg-indigo-50/50 rounded-lg p-3 border border-indigo-100 max-w-sm mx-auto">
                <p className="text-[#475569] text-[11px] leading-relaxed">
                  بروتوكول التكامل الذكي مع **Google Calendar** للعيادة يمنحك مزامنة المواعيد المباشرة، وحجز خانات التشخيص وجدولتها بصورة متزامنة وآمنة مع طبيب المراجع.
                </p>
              </div>

              {/* GSI Material Button Representation */}
              <button
                type="button"
                onClick={handleConnect}
                disabled={isLoggingIn}
                className="gsi-material-button w-full flex items-center justify-center gap-2.5 bg-white border border-[#DADCE0] hover:bg-[#F8FAFC] active:bg-[#F1F3F4] text-[#3C4043] font-bold text-xs py-2.5 px-4 rounded-md shadow-sm transition hover:scale-[101%] duration-200 cursor-pointer"
              >
                {!isLoggingIn ? (
                  <>
                    <svg className="w-4 h-4" viewBox="0 0 48 48">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                    </svg>
                    <span>ربط وتفعيل تقويم Google</span>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin text-indigo-600" />
                    <span>جاري الاتصال بـ Google Secure Server...</span>
                  </div>
                )}
              </button>
            </div>
          ) : (
            // Connected Dashboard Controls
            <div className="space-y-4">
              
              {/* Account details */}
              <div className="bg-slate-50 border border-[#E2E8F0] rounded-lg p-3 flex items-center justify-between text-right">
                <div className="flex items-center gap-2.5">
                  {googleUser?.photoURL ? (
                    <img 
                      src={googleUser.photoURL} 
                      alt="Google account avatar" 
                      className="w-8 h-8 rounded-full border border-indigo-200" 
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-xs">
                      {googleUser?.displayName?.charAt(0) || "G"}
                    </div>
                  )}
                  <div>
                    <h5 className="font-bold text-xs text-slate-800 leading-none">
                      {googleUser?.displayName || "مستخدم العيادة المعتمد"}
                    </h5>
                    <p className="text-[10px] text-slate-500 font-mono mt-0.5">
                      {googleUser?.email}
                    </p>
                  </div>
                </div>
                
                <button
                  type="button"
                  onClick={handleDisconnect}
                  className="p-1 px-2.5 text-rose-600 hover:text-white hover:bg-rose-500 rounded border border-rose-200 hover:border-rose-500 text-[10px] font-bold tracking-wide transition flex items-center gap-1 cursor-pointer"
                  title="إلغاء ربط الحساب"
                >
                  <LogOut className="w-3 h-3" />
                  إلغاء الربط
                </button>
              </div>

              {/* Auto Sync Toggle */}
              <div className="flex items-center justify-between bg-indigo-50/40 border border-indigo-100/60 p-3 rounded-lg">
                <div className="text-right">
                  <span className="block text-xs font-bold text-indigo-950">
                    مزامنة فورية وتلقائية
                  </span>
                  <span className="block text-[10px] text-slate-500 mt-1">
                    مزامنة مواعيد المرضى تلقائياً إلى التقويم فور حجزها.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={toggleAutoSync}
                  className={`w-11 h-6 rounded-full transition-all relative ${
                    autoSyncEnabled ? 'bg-indigo-600' : 'bg-slate-200'
                  }`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full border shadow-sm transition-all ${
                    autoSyncEnabled ? 'left-5.5' : 'left-0.5'
                  }`} />
                </button>
              </div>

              {/* Manual Pending Appointments List */}
              <div className="space-y-2">
                <h4 className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                  مراقبة قائمة المواعيد والتحكم اليدوي
                </h4>

                {appointments.length === 0 ? (
                  <p className="text-[10px] text-slate-400 italic text-center py-2">
                    لا توجد أي مواعيد مسجلة اليوم لتتم مزامنتها.
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-2 border border-slate-100 rounded-lg p-2 bg-slate-50/50">
                    {appointments.slice(-5).reverse().map((appt) => {
                      const patient = patients.find(p => p.id === appt.patientId);
                      const syncedUrl = syncedEventLinks[appt.id];
                      const isCurrentlySyncing = isSyncing === appt.id;

                      return (
                        <div 
                          key={appt.id} 
                          className="bg-white border border-[#E2E8F0] p-2.5 rounded-md flex justify-between items-center text-right"
                        >
                          <div>
                            <h5 className="font-bold text-[11px] text-slate-800">
                              {patient ? patient.name : "لم يتم فك اسم المريض"}
                            </h5>
                            <span className="block text-[9px] text-slate-500 font-medium">
                              {appt.department} • {new Date(appt.dateTime).toLocaleString("ar-IQ", { hour: 'numeric', minute: 'numeric', day: 'numeric', month: 'numeric' })}
                            </span>
                          </div>

                          <div>
                            {syncedUrl ? (
                              <a
                                href={syncedUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-[9px] font-bold px-2 py-1 rounded border border-emerald-200 transition flex items-center gap-1 cursor-pointer"
                              >
                                <ExternalLink className="w-3 h-3" />
                                مكتمل في Google
                              </a>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setShowConfirmation({ appt })}
                                disabled={isCurrentlySyncing}
                                className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 text-white disabled:text-slate-400 text-[9px] font-bold px-2.5 py-1 rounded shadow-sm transition transform active:scale-95 cursor-pointer"
                              >
                                {isCurrentlySyncing ? (
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                ) : (
                                  "مزامنة الآن"
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          )}

        </div>
      )}

      {/* COMPLIANCE CHECK: Explicit User Confirmation Overlay Modal for Google Calendar API mutation */}
      {showConfirmation && (() => {
        const { appt } = showConfirmation;
        const patient = patients.find(p => p.id === appt.patientId);
        const patientName = patient ? patient.name : "مراجع غير معروف";
        const physicianName = appt.physicianId === "dr.ali@clinic.iq" ? "طبيبة أسنان وتجميل - د. جمانة مصطفى الجلبي" : "د.واثق عبدالخالق الجلبي";

        return (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl border border-slate-200 max-w-md w-full p-5 text-right space-y-4 shadow-2xl animate-in scale-in duration-200">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5 text-indigo-700">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <h4 className="font-bold text-xs uppercase tracking-wide">
                  تأكيد مزامنة الموعد الطبي إلى Google Calendar
                </h4>
              </div>

              <div className="space-y-2 text-xs text-slate-600">
                <p>
                  أنت على وشك إضافة وتدوين حدث استشاري طبي جديد ومحمّي في تقويمك الأساسي. تفاصيل التدوين:
                </p>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5 text-[11px] font-medium text-slate-800">
                  <div>• اسم المريض: <strong className="text-indigo-950">{patientName}</strong></div>
                  <div>• القسم الطبي: <span>{appt.department}</span></div>
                  <div>• الطبيب المسؤول: <span>{physicianName}</span></div>
                  <div>• التوقيت: <span>{new Date(appt.dateTime).toLocaleString("ar")}</span></div>
                </div>
                <p className="text-[10px] text-slate-500 leading-normal">
                  * هذا الإجراء يلتزم بمعايير السرّية الطبية لبيانات المرضى ولا ينشر تفاصيل الحالة السريرية خارج مراجعة الطبيب.
                </p>
              </div>

              <div className="flex justify-end gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setShowConfirmation(null)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg transition duration-150 cursor-pointer"
                >
                  إلغاء العملية
                </button>
                <button
                  type="button"
                  onClick={() => executeSync(appt)}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition duration-150 shadow-md cursor-pointer flex items-center gap-1"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  مزامنة وتدوين الفحص
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
